import { useState } from "react";
import {
  Building2, CheckCircle2, Circle, TrendingUp, TrendingDown, Sparkles, FileText,
  Plus, Trash2, ExternalLink, Banknote, AlertTriangle, ArrowRight, ChevronDown,
  Download, RefreshCw, Check, ShieldAlert, Landmark,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Field } from "@/pages/formation/FormationSteps";

const ELIG_STYLE = {
  Likely: "bg-emerald-500/15 text-emerald-400", Possible: "bg-[#D4AF37]/15 text-[#D4AF37]",
  Situational: "bg-blue-500/15 text-blue-400", Early: "bg-white/8 text-[#A1A1AA]", Unlikely: "bg-red-500/12 text-red-400",
};

export function ImportStep({ wf }) {
  const im = wf.imported;
  const rows = [["Business", im.name || "—"], ["Entity", im.entity || "—"], ["State", im.state || "—"],
    ["Industry", im.industry || "—"], ["EIN", im.has_ein ? "On file" : "Not set"],
    ["Business bank", im.has_bank ? "Yes" : "No"], ["D-U-N-S", im.duns ? "Yes" : "No"],
    ["Business credit", `${im.business_credit} (${im.credit_tier})`]];
  return (
    <div data-testid="funding-step-import">
      {im.has_profile ? (
        <div className="rounded-2xl border border-[#D4AF37]/25 bg-gradient-to-br from-[#151310] to-[#121214] p-6" data-testid="funding-imported">
          <div className="flex items-center gap-2 mb-4"><Building2 className="w-5 h-5 text-[#D4AF37]" /><h3 className="font-heading text-lg text-white">Imported from your Business Profile & Credit</h3></div>
          <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
            {rows.map(([k, v]) => <div key={k} className="flex justify-between border-b border-white/5 py-1"><span className="text-[#71717A]">{k}</span><span className="text-white">{v}</span></div>)}
          </div>
          <p className="text-xs text-[#71717A] mt-4">You won't need to re-enter any of this. Next we'll ask only for funding-specific details.</p>
        </div>
      ) : (
        <div className="rounded-xl border border-[#D4AF37]/30 bg-[#151310] p-4 text-sm text-[#D4D4D8]" data-testid="funding-no-profile">
          Tip: complete <span className="text-[#D4AF37]">Business Formation</span> and <span className="text-[#D4AF37]">Business Credit</span> first so we can auto-import your details and score readiness accurately.
        </div>
      )}
    </div>
  );
}

export function IntakeStep({ intake, setIntake, wf }) {
  const set = (k, v) => setIntake({ ...intake, [k]: v });
  const toggleDoc = (id) => {
    const cur = intake.documents_available || [];
    set("documents_available", cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]);
  };
  return (
    <div className="space-y-4" data-testid="funding-step-intake">
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Requested funding amount ($)" type="number" value={intake.requested_amount} onChange={(v) => set("requested_amount", v)} testid="intake-amount" />
        <Field label="Intended use of funds" value={intake.use_of_funds} onChange={(v) => set("use_of_funds", v)} placeholder="e.g. equipment, hiring, inventory" testid="intake-use" />
        <Field label="Annual revenue ($)" type="number" value={intake.annual_revenue} onChange={(v) => set("annual_revenue", v)} testid="intake-annual" />
        <Field label="Avg. monthly bank deposits ($)" type="number" value={intake.avg_monthly_deposits} onChange={(v) => set("avg_monthly_deposits", v)} testid="intake-deposits" />
        <Field label="Monthly expenses ($)" type="number" value={intake.monthly_expenses} onChange={(v) => set("monthly_expenses", v)} testid="intake-expenses" />
        <Field label="Time in business (months)" type="number" value={intake.time_in_business_months} onChange={(v) => set("time_in_business_months", v)} testid="intake-tib" />
        <label className="block"><span className="text-xs text-[#A1A1AA] mb-1.5 block">Personal credit range</span>
          <Select value={intake.personal_credit_range || ""} onValueChange={(v) => set("personal_credit_range", v)}>
            <SelectTrigger className="bg-[#0d0d0f] border-white/10 text-white h-11" data-testid="intake-pcredit"><SelectValue placeholder="Select range" /></SelectTrigger>
            <SelectContent className="bg-[#1C1C1F] border-white/10 text-white">{wf.personal_credit_ranges.map((r) => <SelectItem key={r} value={r} className="focus:bg-white/10">{r}</SelectItem>)}</SelectContent>
          </Select>
        </label>
        <Field label="Existing business debt ($)" type="number" value={intake.existing_debt} onChange={(v) => set("existing_debt", v)} testid="intake-debt" />
        <Field label="Number of employees" type="number" value={intake.employees} onChange={(v) => set("employees", v)} testid="intake-employees" />
        <Field label="Ownership %" type="number" value={intake.ownership_pct} onChange={(v) => set("ownership_pct", v)} testid="intake-ownership" />
      </div>
      <div className="flex flex-wrap gap-3">
        {[["collateral", "Collateral available"], ["profitable", "Currently profitable"], ["derogatory", "Recent bankruptcy/lien/judgment"]].map(([k, label]) => (
          <button key={k} onClick={() => set(k, !intake[k])} data-testid={`intake-${k}`}
            className={`flex items-center gap-2 rounded-full px-4 py-2 text-sm border transition-colors ${intake[k] ? "border-[#D4AF37]/50 bg-[#151310] text-white" : "border-white/10 bg-[#121214] text-[#A1A1AA] hover:border-white/20"}`}>
            {intake[k] ? <CheckCircle2 className="w-4 h-4 text-[#D4AF37]" /> : <Circle className="w-4 h-4" />} {label}
          </button>
        ))}
      </div>
      <div className="rounded-xl border border-white/8 bg-[#0f0f11] p-4">
        <div className="text-xs uppercase tracking-wider text-[#71717A] mb-3">Financial documents you already have</div>
        <div className="grid sm:grid-cols-2 gap-2">
          {wf.doc_checklist.map((d) => {
            const on = (intake.documents_available || []).includes(d.id);
            return (
              <button key={d.id} onClick={() => toggleDoc(d.id)} data-testid={`intake-doc-${d.id}`}
                className={`text-left flex items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors ${on ? "border-[#D4AF37]/30 bg-[#151310] text-white" : "border-white/8 text-[#A1A1AA] hover:border-white/20"}`}>
                {on ? <CheckCircle2 className="w-4 h-4 text-[#D4AF37] shrink-0" /> : <Circle className="w-4 h-4 shrink-0" />}{d.label}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export function ReadinessStep({ wf }) {
  const r = wf.readiness;
  const helping = r.factors.filter((f) => f.helping);
  const hurting = r.factors.filter((f) => !f.helping);
  return (
    <div data-testid="funding-step-readiness">
      <div className="rounded-2xl border border-[#D4AF37]/25 bg-gradient-to-br from-[#151310] to-[#121214] p-6 mb-5 flex items-center gap-5">
        <div className="relative w-24 h-24 shrink-0">
          <svg viewBox="0 0 36 36" className="w-24 h-24 -rotate-90"><circle cx="18" cy="18" r="15.9" fill="none" stroke="#ffffff12" strokeWidth="3" /><circle cx="18" cy="18" r="15.9" fill="none" stroke="#D4AF37" strokeWidth="3" strokeDasharray={`${r.score}, 100`} strokeLinecap="round" /></svg>
          <div className="absolute inset-0 flex items-center justify-center font-heading text-2xl text-white" data-testid="funding-readiness-score">{r.score}%</div>
        </div>
        <div>
          <div className="font-heading text-2xl text-white">{r.label}</div>
          <div className="text-sm text-[#A1A1AA]">Estimated funding range: <span className="text-[#D4AF37]">{r.estimated_range}</span></div>
          <p className="text-[11px] text-[#52525B] mt-2 max-w-md">{wf.disclaimer}</p>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-4" data-testid="readiness-helping">
          <div className="flex items-center gap-2 mb-3 text-emerald-400"><TrendingUp className="w-4 h-4" /><span className="text-sm font-semibold">Helping your eligibility</span></div>
          {helping.map((f) => <div key={f.key} className="flex items-start gap-2 text-sm text-[#D4D4D8] mb-2"><Check className="w-3.5 h-3.5 text-emerald-400 mt-0.5 shrink-0" /><span><b className="text-white">{f.label}.</b> {f.detail}</span></div>)}
          {!helping.length && <p className="text-xs text-[#71717A]">Complete the intake to build your strengths.</p>}
        </div>
        <div className="rounded-xl border border-[#D4AF37]/20 bg-[#151310] p-4" data-testid="readiness-hurting">
          <div className="flex items-center gap-2 mb-3 text-[#D4AF37]"><TrendingDown className="w-4 h-4" /><span className="text-sm font-semibold">Hurting / to improve</span></div>
          {hurting.map((f) => <div key={f.key} className="flex items-start gap-2 text-sm text-[#D4D4D8] mb-2"><AlertTriangle className="w-3.5 h-3.5 text-[#D4AF37] mt-0.5 shrink-0" /><span><b className="text-white">{f.label}.</b> {f.detail}</span></div>)}
          {!hurting.length && <p className="text-xs text-emerald-400">No major gaps — you're in great shape!</p>}
        </div>
      </div>
    </div>
  );
}

export function MatchesStep({ wf }) {
  const [open, setOpen] = useState(null);
  return (
    <div data-testid="funding-step-matches">
      <p className="text-xs text-[#71717A] mb-4">{wf.disclaimer}</p>
      <div className="space-y-2">
        {wf.matches.map((m) => {
          const expanded = open === m.id;
          return (
            <div key={m.id} className="rounded-2xl border border-white/8 bg-[#121214]" data-testid={`match-${m.id}`}>
              <button onClick={() => setOpen(expanded ? null : m.id)} data-testid={`match-expand-${m.id}`} className="w-full text-left flex items-center gap-3 p-4">
                <Banknote className="w-5 h-5 text-[#D4AF37] shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap"><span className="text-white font-medium">{m.name}</span><span className={`text-[10px] px-2 py-0.5 rounded-full ${ELIG_STYLE[m.eligibility]}`}>{m.eligibility} eligibility</span></div>
                  <div className="text-xs text-[#71717A] mt-0.5">{m.range} · {m.match_reason}</div>
                </div>
                <ChevronDown className={`w-4 h-4 text-[#71717A] shrink-0 transition-transform ${expanded ? "rotate-180" : ""}`} />
              </button>
              {expanded && (
                <div className="px-4 pb-4 space-y-2 text-sm" data-testid={`match-detail-${m.id}`}>
                  <Detail label="Typical requirements" value={m.requirements} />
                  <Detail label="Repayment" value={m.repayment} />
                  <div className="grid sm:grid-cols-2 gap-2">
                    <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/5 p-3"><div className="text-[11px] uppercase text-emerald-400 mb-1">Advantages</div>{m.advantages.map((a) => <div key={a} className="text-xs text-[#D4D4D8]">+ {a}</div>)}</div>
                    <div className="rounded-lg border border-red-500/20 bg-red-500/5 p-3"><div className="text-[11px] uppercase text-red-400 mb-1">Disadvantages</div>{m.disadvantages.map((a) => <div key={a} className="text-xs text-[#D4D4D8]">- {a}</div>)}</div>
                  </div>
                  <Detail label="Required documents" value={m.documents.join(", ")} />
                  <Detail label="Steps before applying" value={m.steps_before.join(" · ")} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
function Detail({ label, value }) {
  return <div><span className="text-[11px] uppercase tracking-wider text-[#D4AF37]">{label}: </span><span className="text-[#A1A1AA]">{value}</span></div>;
}

export function DocumentsStep({ wf, onSetStatus }) {
  const navigate = useNavigate();
  const STATUS_STYLE = { available: "text-emerald-400", uploaded: "text-emerald-400", missing: "text-[#71717A]", expired: "text-red-400", under_review: "text-[#D4AF37]" };
  return (
    <div data-testid="funding-step-documents">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-[#A1A1AA]">Mark each document's status. Upload files in your Document Library — they connect to your applications.</p>
        <button onClick={() => navigate("/documents")} data-testid="documents-library-link" className="text-sm text-[#D4AF37] hover:underline flex items-center gap-1 shrink-0">Document Library <ExternalLink className="w-3.5 h-3.5" /></button>
      </div>
      <div className="space-y-2">
        {wf.doc_checklist.map((d) => {
          const status = wf.documents[d.id] || "missing";
          return (
            <div key={d.id} className="flex items-center gap-3 rounded-xl border border-white/8 bg-[#121214] p-3.5" data-testid={`doc-row-${d.id}`}>
              <FileText className={`w-4 h-4 shrink-0 ${STATUS_STYLE[status]}`} />
              <span className="text-sm text-white flex-1">{d.label}</span>
              <Select value={status} onValueChange={(v) => onSetStatus(d.id, v)}>
                <SelectTrigger className="bg-[#0d0d0f] border-white/10 text-white h-8 w-40 text-xs" data-testid={`doc-status-${d.id}`}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#1C1C1F] border-white/10 text-white">{wf.doc_statuses.map((s) => <SelectItem key={s} value={s} className="focus:bg-white/10 text-xs">{s.replace("_", " ")}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function ApplicationsStep({ wf, onAdd, onUpdate, onDelete }) {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ provider: "", funding_type: "", requested_amount: "", status: "Researching", follow_up_date: "" });
  const [plans, setPlans] = useState({});
  const apps = wf.applications || [];
  const submit = () => {
    if (!form.provider) return;
    onAdd({ ...form, requested_amount: parseFloat(form.requested_amount) || 0 });
    setForm({ provider: "", funding_type: "", requested_amount: "", status: "Researching", follow_up_date: "" });
    setShowForm(false);
  };
  const changeStatus = async (app, status) => {
    let reason = "";
    if (status === "Declined") { reason = window.prompt("Decline reason? The AI Advisor will build an improvement plan.", "") || ""; }
    const res = await onUpdate(app.app_id, { status, ...(reason ? { decline_reason: reason } : {}) });
    if (res?.improvement_plan) setPlans((p) => ({ ...p, [app.app_id]: res.improvement_plan }));
  };
  return (
    <div data-testid="funding-step-applications">
      {!showForm && <button onClick={() => setShowForm(true)} data-testid="app-add-btn" className="flex items-center gap-1.5 text-sm h-10 px-4 rounded-full bg-white/5 border border-white/15 text-white hover:bg-white/10 transition-colors mb-4"><Plus className="w-4 h-4" /> Add application</button>}
      {showForm && (
        <div className="rounded-2xl border border-[#D4AF37]/25 bg-[#151310] p-5 mb-4" data-testid="app-form">
          <div className="grid sm:grid-cols-2 gap-3 mb-3">
            <Field label="Funding provider" value={form.provider} onChange={(v) => setForm({ ...form, provider: v })} testid="app-provider" />
            <Field label="Funding type" value={form.funding_type} onChange={(v) => setForm({ ...form, funding_type: v })} placeholder="e.g. Term Loan" testid="app-type" />
            <Field label="Requested amount ($)" type="number" value={form.requested_amount} onChange={(v) => setForm({ ...form, requested_amount: v })} testid="app-amount" />
            <label className="block"><span className="text-xs text-[#A1A1AA] mb-1.5 block">Status</span>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="bg-[#0d0d0f] border-white/10 text-white h-11" data-testid="app-status-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#1C1C1F] border-white/10 text-white max-h-64">{wf.app_statuses.map((s) => <SelectItem key={s} value={s} className="focus:bg-white/10">{s}</SelectItem>)}</SelectContent>
              </Select>
            </label>
            <Field label="Follow-up date" type="date" value={form.follow_up_date} onChange={(v) => setForm({ ...form, follow_up_date: v })} testid="app-followup" />
          </div>
          <div className="flex gap-2"><button onClick={submit} data-testid="app-save-btn" className="fv-gold-gradient text-[#0A0A0B] rounded-full px-5 py-2 text-sm font-semibold">Save</button><button onClick={() => setShowForm(false)} className="rounded-full px-4 py-2 text-sm text-[#A1A1AA] hover:text-white">Cancel</button></div>
        </div>
      )}
      <div className="space-y-2" data-testid="app-list">
        {apps.length === 0 && <p className="text-sm text-[#52525B]">No applications tracked yet.</p>}
        {apps.map((a) => (
          <div key={a.app_id} className="rounded-xl border border-white/8 bg-[#121214] p-4" data-testid={`app-${a.app_id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="text-white font-medium">{a.provider} <span className="text-xs text-[#71717A]">· {a.funding_type || "—"}</span></div>
                <div className="text-xs text-[#71717A] mt-0.5">Requested ${(a.requested_amount || 0).toLocaleString()}{a.follow_up_date && ` · Follow-up ${a.follow_up_date}`}{a.approved_amount ? ` · Approved $${a.approved_amount.toLocaleString()}` : ""}</div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Select value={a.status} onValueChange={(v) => changeStatus(a, v)}>
                  <SelectTrigger className="bg-[#0d0d0f] border-white/10 text-white h-8 w-44 text-xs" data-testid={`app-status-${a.app_id}`}><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#1C1C1F] border-white/10 text-white max-h-64">{wf.app_statuses.map((s) => <SelectItem key={s} value={s} className="focus:bg-white/10 text-xs">{s}</SelectItem>)}</SelectContent>
                </Select>
                <button onClick={() => onDelete(a.app_id)} data-testid={`app-delete-${a.app_id}`} className="text-[#71717A] hover:text-red-400 transition-colors"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
            {plans[a.app_id] && (
              <div className="mt-3 rounded-lg border border-[#D4AF37]/25 bg-[#151310] p-3" data-testid={`app-improvement-${a.app_id}`}>
                <div className="flex items-center gap-1.5 text-[#D4AF37] text-xs font-semibold mb-1"><Sparkles className="w-3.5 h-3.5" /> AI improvement plan</div>
                {plans[a.app_id].map((s, i) => <div key={i} className="text-xs text-[#D4D4D8] flex gap-1.5"><ArrowRight className="w-3 h-3 mt-0.5 shrink-0 text-[#D4AF37]" />{s}</div>)}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export function GenerateStep({ hasDocs, onGenerate, generating }) {
  const DOCS = ["Funding Readiness Report", "Funding Preparation Checklist", "Funding Use Statement", "Lender-Ready Business Summary", "Debt Schedule", "Application Preparation Checklist", "Financial Document Checklist", "Funding Improvement Plan", "Grant Preparation Checklist", "Application Follow-Up Letter"];
  return (
    <div data-testid="funding-step-generate">
      <p className="text-sm text-[#A1A1AA] mb-4">Generate lender-ready documents from your real saved information — all saved to your Document Library.</p>
      <div className="grid sm:grid-cols-2 gap-2 mb-5">
        {DOCS.map((d) => <div key={d} className="flex items-center gap-3 rounded-xl border border-white/8 bg-[#121214] p-3.5" data-testid={`fgen-${d.replace(/\s+/g, '-').toLowerCase()}`}><FileText className={`w-4 h-4 shrink-0 ${hasDocs ? "text-[#D4AF37]" : "text-[#52525B]"}`} /><span className="text-sm text-[#D4D4D8] flex-1">{d}</span>{hasDocs && <Check className="w-4 h-4 text-[#D4AF37]" />}</div>)}
      </div>
      <button onClick={onGenerate} disabled={generating} data-testid="funding-generate-btn" className="flex items-center gap-2 fv-gold-gradient text-[#0A0A0B] rounded-full px-5 py-2.5 text-sm font-semibold disabled:opacity-50">
        {generating ? "Generating…" : hasDocs ? <><RefreshCw className="w-4 h-4" /> Regenerate</> : <><Download className="w-4 h-4" /> Generate documents</>}
      </button>
      {hasDocs && <p className="text-xs text-emerald-400 mt-3" data-testid="funding-docs-saved-note">✓ Saved to your Document Library.</p>}
    </div>
  );
}

export function FundingReviewStep({ review, onComplete, completing }) {
  return (
    <div data-testid="funding-step-review">
      <div className="rounded-2xl border border-[#D4AF37]/25 bg-gradient-to-br from-[#151310] to-[#121214] p-6 mb-4 flex items-center gap-4">
        <div className="w-12 h-12 rounded-xl fv-gold-gradient flex items-center justify-center shrink-0"><Sparkles className="w-6 h-6 text-[#0A0A0B]" /></div>
        <div><div className="font-heading text-lg text-white">AI funding review</div><div className="text-sm text-[#A1A1AA]">Readiness <span className="text-[#D4AF37] font-semibold" data-testid="funding-review-readiness">{review.funding_readiness}%</span> · Best path: <span className="text-white">{review.best_path}</span></div></div>
      </div>
      {review.recommendations.map((r, i) => <Block key={i} icon={CheckCircle2} color="text-emerald-400" title={r.title} items={[r.detail]} testid={`review-rec-${i}`} />)}
      {review.missing_requirements.length > 0 && <Block icon={AlertTriangle} color="text-[#D4AF37]" title="Missing requirements" items={review.missing_requirements} testid="review-missing" />}
      {review.cautions.length > 0 && <Block icon={ShieldAlert} color="text-red-400" title="Cautions" items={review.cautions} testid="review-cautions" />}
      <button onClick={onComplete} disabled={completing} data-testid="funding-complete-btn" className="mt-4 flex items-center gap-2 fv-gold-gradient text-[#0A0A0B] rounded-full px-6 py-3 text-sm font-semibold disabled:opacity-50">
        {completing ? "Finishing…" : <>Finish & save Funding Readiness Report <ArrowRight className="w-4 h-4" /></>}
      </button>
    </div>
  );
}
function Block({ icon: Icon, color, title, items, testid }) {
  return (
    <div className="rounded-xl border border-white/8 bg-[#121214] p-5 mb-3" data-testid={testid}>
      <div className={`flex items-center gap-2 mb-2 ${color}`}><Icon className="w-4 h-4" /><span className="text-sm font-semibold">{title}</span></div>
      <ul className="space-y-1.5">{items.map((it, i) => <li key={i} className="flex items-start gap-2 text-sm text-[#A1A1AA]"><Circle className="w-1.5 h-1.5 mt-1.5 shrink-0 fill-current" />{it}</li>)}</ul>
    </div>
  );
}

export function FundingDashboard({ d }) {
  const tiles = [["Readiness", `${d.readiness_score}%`], ["Est. range", d.estimated_range.replace(" (estimated)", "")],
    ["In progress", d.in_progress], ["Approved", d.approved], ["Requested", `$${(d.total_requested || 0).toLocaleString()}`],
    ["Total approved", `$${(d.total_approved || 0).toLocaleString()}`], ["Docs needed", d.documents_needed]];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="funding-dashboard">
      {tiles.map(([k, v]) => <div key={k} className="rounded-xl border border-white/8 bg-[#121214] p-3"><div className="font-heading text-lg text-white truncate">{v}</div><div className="text-[10px] uppercase tracking-wider text-[#71717A]">{k}</div></div>)}
    </div>
  );
}

export { Landmark };
